import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  FileText, RefreshCw, Download, Search, X, Euro, Percent,
  AlertCircle, ArrowUp, ArrowDown, FilterX, Scale, FileSpreadsheet, Printer,
} from "lucide-react";
import styles from "../ReportShared.module.css";
import { useToast } from "../../../context/ToastContext";
import { getInvoiceReport } from "../../../api/reports/useReportsApi";
import {
  formatEUR, formatReportDate, sortRows,
  downloadCSV, downloadExcel, printHtml, filtersToPrintHtml, buildFilterLines,
} from "../reportUtils";

// Human-readable labels for the document-type filter, used in export headers.
const DOC_TYPE_UI = {
  sales_invoice: "Sales Invoice",
  credit_invoice: "Credit Note",
  debit_notes: "Debit Note",
};

const EMPTY_SUMMARY = {
  totalDocuments: 0, invoiceCount: 0, creditNoteCount: 0, debitNoteCount: 0,
  totalInvoiced: 0, totalCredited: 0, totalDebited: 0, netAmount: 0,
  totalVat: 0, normalVatCount: 0, tomsCount: 0,
};

const INITIAL_FILTERS = {
  dateFrom: "", dateTo: "", docType: "", vatType: "", status: "", search: "",
};

const DocTypeBadge = ({ subtype, label }) => {
  const cls =
    subtype === "credit_invoice" ? styles.docCredit :
    subtype === "debit_notes" ? styles.docDebit : styles.docInvoice;
  return <span className={`${styles.docBadge} ${cls}`}>{label}</span>;
};

const VatChip = ({ vatType }) => {
  const cls =
    vatType === "TOMS" ? styles.vatToms :
    vatType === "Normal VAT" ? styles.vatNormal : styles.vatNone;
  const label = vatType === "TOMS" ? "TOMS (margin)" : vatType;
  return <span className={`${styles.vatChip} ${cls}`}>{label}</span>;
};

const StatusBadge = ({ status }) => {
  const cls =
    status === "Sent" ? styles.badgeConfirmed :
    status === "Draft" ? styles.badgePending : styles.badgeIssued;
  return (
    <span className={`${styles.badge} ${cls}`}>
      <span className={styles.badgeDot} />
      {status}
    </span>
  );
};

// An invoice freezes its amounts when generated; editing the booking afterwards
// never rewrites it. When what the booking has been invoiced (net of credit and
// debit notes) no longer equals its total, the invoice predates a change and
// needs a regenerate or a credit note.
const StaleChip = ({ row }) => {
  if (!row.stale) return null;
  const title =
    `Invoiced ${formatEUR(row.netInvoiced)} but booking totals ${formatEUR(row.bookingTotal)}. ` +
    `The booking changed after this invoice was issued — regenerate it or issue a credit note.`;
  return (
    <span className={styles.staleChip} title={title}>
      Out of date
    </span>
  );
};

const SortableTh = ({ label, sortKey, sort, onSort, align }) => {
  const active = sort.key === sortKey;
  return (
    <th
      className={`${styles.thSortable} ${active ? styles.thSorted : ""} ${align === "right" ? styles.thRight : ""} ${align === "center" ? styles.thCenter : ""}`}
      onClick={() => onSort(sortKey)}
    >
      {label}
      {active && (
        <span className={styles.sortArrow}>
          {sort.dir === "asc" ? <ArrowUp size={11} /> : <ArrowDown size={11} />}
        </span>
      )}
    </th>
  );
};

const InvoiceReport = () => {
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sort, setSort] = useState({ key: "issueDate", dir: "desc" });

  const debounceRef = useRef(null);
  const firstLoadRef = useRef(true);

  const fetchReport = useCallback(async (activeFilters, { announce = false } = {}) => {
    setLoading(true);
    setError("");
    try {
      const res = await getInvoiceReport(activeFilters);
      if (!res?.success) throw new Error(res?.message || "Failed to load invoice report");
      setRows(Array.isArray(res.data?.rows) ? res.data.rows : []);
      setSummary(res.data?.summary || EMPTY_SUMMARY);
      if (announce) showToast("Invoice report loaded", "success");
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Failed to load invoice report";
      setError(msg);
      setRows([]);
      setSummary(EMPTY_SUMMARY);
      showToast(msg, "error");
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    if (firstLoadRef.current) {
      firstLoadRef.current = false;
      fetchReport(INITIAL_FILTERS, { announce: true });
      return undefined;
    }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchReport(filters), 400);
    return () => clearTimeout(debounceRef.current);
  }, [filters, fetchReport]);

  const setFilter = (key) => (e) =>
    setFilters((prev) => ({ ...prev, [key]: e.target.value }));

  const hasActiveFilters = useMemo(
    () => Object.values(filters).some((v) => String(v).trim() !== ""),
    [filters]
  );

  const clearFilters = () => setFilters(INITIAL_FILTERS);
  const handleRefresh = () => fetchReport(filters, { announce: true });

  const onSort = (key) =>
    setSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { key, dir: "asc" }
    );

  const sortedRows = useMemo(() => sortRows(rows, sort.key, sort.dir), [rows, sort]);

  // ── Export (CSV / Excel / PDF / Print) ──
  const EXPORT_HEADERS = [
    "Document #", "Type", "Linked Invoice", "Customer", "Booking Ref",
    "Issue Date", "VAT Type", "Subtotal", "VAT Amount", "TOMS Margin (VAT basis)",
    "Total", "Status",
  ];
  // Right-aligned numeric columns in the printed table.
  const NUM_COLS = new Set([7, 8, 9, 10]);

  const exportMatrix = () =>
    sortedRows.map((r) => [
      r.invoiceNumber || "",
      r.docTypeLabel || "",
      r.linkedInvoiceNumber || "",
      r.customerName || "",
      r.bookingReference || "",
      formatReportDate(r.issueDate),
      r.vatType || "",
      formatEUR(r.subtotal),
      formatEUR(r.vatAmount),
      r.vatType === "TOMS" ? formatEUR(r.tomsMargin) : "",
      formatEUR(r.amount),
      r.status || "",
    ]);

  // Only the criteria the user actually set — shown on the Excel sheet + PDF.
  const appliedFilters = () =>
    buildFilterLines([
      ["Issue date from", filters.dateFrom],
      ["Issue date to", filters.dateTo],
      ["Document type", DOC_TYPE_UI[filters.docType] || ""],
      ["VAT type", filters.vatType],
      ["Status", filters.status],
      ["Search", filters.search],
    ]);

  const guardEmpty = () => {
    if (!sortedRows.length) {
      showToast("Nothing to export — no documents match the current filters", "error");
      return true;
    }
    return false;
  };

  const stamp = new Date().toISOString().slice(0, 10);

  const handleCsv = () => {
    if (guardEmpty()) return;
    try {
      downloadCSV(`invoice-report-${stamp}.csv`, EXPORT_HEADERS, exportMatrix());
      showToast(`Exported ${sortedRows.length} document rows to CSV`, "success");
    } catch {
      showToast("CSV export failed", "error");
    }
  };

  const handleExcel = () => {
    if (guardEmpty()) return;
    try {
      downloadExcel(
        `invoice-report-${stamp}.xls`,
        EXPORT_HEADERS,
        exportMatrix(),
        "Invoice Report",
        appliedFilters()
      );
      showToast("Excel exported", "success");
    } catch {
      showToast("Excel export failed", "error");
    }
  };

  const buildPrintHtml = () => {
    const esc = (v) => String(v ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;");
    const kpis = [
      ["Documents", summary.totalDocuments],
      ["Total Invoiced", formatEUR(summary.totalInvoiced)],
      ["Credited", formatEUR(summary.totalCredited)],
      ["Debited", formatEUR(summary.totalDebited)],
      ["Net Amount", formatEUR(summary.netAmount)],
      ["VAT Amount", formatEUR(summary.totalVat)],
    ]
      .map(([l, v]) => `<div class="kpi"><div class="l">${esc(l)}</div><div class="v">${esc(v)}</div></div>`)
      .join("");
    const head = EXPORT_HEADERS
      .map((h, i) => `<th${NUM_COLS.has(i) ? ' class="r"' : ""}>${esc(h)}</th>`)
      .join("");
    const body = exportMatrix()
      .map(
        (r) =>
          `<tr>${r
            .map((c, i) => `<td${NUM_COLS.has(i) ? ' class="r"' : ""}>${esc(c)}</td>`)
            .join("")}</tr>`
      )
      .join("");
    return `<h1>Invoice Report</h1>
      <p class="sub">Sales invoices, credit notes and debit notes · Generated ${esc(new Date().toLocaleString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false }))} · ${sortedRows.length} document${sortedRows.length === 1 ? "" : "s"}</p>
      ${filtersToPrintHtml(appliedFilters())}
      <div class="kpis">${kpis}</div>
      <table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>`;
  };

  const handlePdf = () => {
    if (guardEmpty()) return;
    if (!printHtml(buildPrintHtml(), "Invoice Report")) {
      showToast("Allow pop-ups to export the PDF, then try again", "error");
      return;
    }
    showToast("Opening print dialog — choose “Save as PDF”", "success");
  };

  const handlePrint = () => {
    if (guardEmpty()) return;
    if (!printHtml(buildPrintHtml(), "Invoice Report")) {
      showToast("Allow pop-ups to print, then try again", "error");
      return;
    }
    showToast("Opening print dialog", "success");
  };

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.pageHeader}>
        <div className={styles.headerLeft}>
          <div className={styles.headerIcon}><FileText size={24} /></div>
          <div className={styles.headerText}>
            <h1>
              Invoice Report
              <span className={styles.readOnlyTag}>Read-only</span>
            </h1>
            <p>Financial reporting of sales invoices, credit notes and debit notes</p>
          </div>
        </div>
        <div className={styles.headerActions}>
          <button className={styles.refreshBtn} onClick={handleRefresh} disabled={loading}>
            <RefreshCw size={15} className={loading ? styles.spinning : ""} />
            Refresh
          </button>
        </div>
      </div>

      {/* KPI tiles */}
      <div className={styles.kpiGrid}>
        <div className={`${styles.kpiCard} ${styles.kpiOrange}`}>
          <div className={styles.kpiTop}>
            <span className={styles.kpiLabel}>Documents</span>
            <span className={styles.kpiIcon}><FileText size={15} /></span>
          </div>
          <span className={styles.kpiValue}>{summary.totalDocuments}</span>
          <span className={styles.kpiSub}>
            <span><b>{summary.invoiceCount}</b> invoices</span>
            <span className={styles.kpiSubCancelled}><b>{summary.creditNoteCount}</b> CN</span>
            <span className={styles.kpiSubPending}><b>{summary.debitNoteCount}</b> DN</span>
          </span>
        </div>
        <div className={`${styles.kpiCard} ${styles.kpiGreen}`}>
          <div className={styles.kpiTop}>
            <span className={styles.kpiLabel}>Total Invoiced</span>
            <span className={styles.kpiIcon}><Euro size={15} /></span>
          </div>
          <span className={styles.kpiValue}>{formatEUR(summary.totalInvoiced)}</span>
          <span className={styles.kpiSub}>sales invoices, gross</span>
        </div>
        <div className={`${styles.kpiCard} ${styles.kpiSlate}`}>
          <div className={styles.kpiTop}>
            <span className={styles.kpiLabel}>Credited · Debited</span>
            <span className={styles.kpiIcon}><Scale size={15} /></span>
          </div>
          <span className={styles.kpiValue}>
            {formatEUR(summary.totalCredited)}
          </span>
          <span className={styles.kpiSub}>
            <span>+ {formatEUR(summary.totalDebited)} debit notes</span>
          </span>
        </div>
        <div className={`${styles.kpiCard} ${styles.kpiBlue}`}>
          <div className={styles.kpiTop}>
            <span className={styles.kpiLabel}>Net Amount</span>
            <span className={styles.kpiIcon}><Euro size={15} /></span>
          </div>
          <span className={styles.kpiValue}>{formatEUR(summary.netAmount)}</span>
          <span className={styles.kpiSub}>invoiced − credited + debited</span>
        </div>
        <div className={`${styles.kpiCard} ${styles.kpiPurple}`}>
          <div className={styles.kpiTop}>
            <span className={styles.kpiLabel}>VAT Amount</span>
            <span className={styles.kpiIcon}><Percent size={15} /></span>
          </div>
          <span className={styles.kpiValue}>{formatEUR(summary.totalVat)}</span>
          <span className={styles.kpiSub}>
            <span><b>{summary.normalVatCount}</b> Normal VAT</span>
            <span><b>{summary.tomsCount}</b> TOMS</span>
          </span>
        </div>
      </div>

      {/* Filters */}
      <div className={styles.filterBar}>
        <div className={styles.filterField}>
          <span className={styles.filterLabel}>Issue date from</span>
          <input type="date" className={styles.filterInput} value={filters.dateFrom} onChange={setFilter("dateFrom")} />
        </div>
        <div className={styles.filterField}>
          <span className={styles.filterLabel}>Issue date to</span>
          <input type="date" className={styles.filterInput} value={filters.dateTo} onChange={setFilter("dateTo")} />
        </div>
        <div className={styles.filterField}>
          <span className={styles.filterLabel}>Document type</span>
          <select className={styles.filterSelect} value={filters.docType} onChange={setFilter("docType")}>
            <option value="">All</option>
            <option value="sales_invoice">Sales Invoice</option>
            <option value="credit_invoice">Credit Note</option>
            <option value="debit_notes">Debit Note</option>
          </select>
        </div>
        <div className={styles.filterField}>
          <span className={styles.filterLabel}>VAT type</span>
          <select className={styles.filterSelect} value={filters.vatType} onChange={setFilter("vatType")}>
            <option value="">All</option>
            <option value="Normal VAT">Normal VAT</option>
            <option value="TOMS">TOMS (margin scheme)</option>
          </select>
        </div>
        <div className={styles.filterField}>
          <span className={styles.filterLabel}>Status</span>
          <select className={styles.filterSelect} value={filters.status} onChange={setFilter("status")}>
            <option value="">All</option>
            <option value="Issued">Issued</option>
            <option value="Sent">Sent</option>
            <option value="Draft">Draft</option>
          </select>
        </div>
        <div className={styles.filterField}>
          <span className={styles.filterLabel}>Search</span>
          <div className={styles.searchWrap}>
            <Search size={15} className={styles.searchIcon} />
            <input
              type="text"
              className={styles.searchInput}
              placeholder="Number, customer, booking…"
              value={filters.search}
              onChange={setFilter("search")}
            />
            {filters.search && (
              <button
                className={styles.searchClear}
                onClick={() => setFilters((p) => ({ ...p, search: "" }))}
              >
                <X size={14} />
              </button>
            )}
          </div>
        </div>
        {hasActiveFilters && (
          <button className={styles.clearFiltersBtn} onClick={clearFilters}>
            <FilterX size={14} />
            Clear filters
          </button>
        )}
      </div>

      {/* Meta */}
      <div className={styles.resultsSummary}>
        Showing <strong>{sortedRows.length}</strong> document{sortedRows.length === 1 ? "" : "s"}
        {hasActiveFilters ? " matching the current filters" : ""}
      </div>

      {error && (
        <div className={styles.errorBox}>
          <AlertCircle size={17} />
          {error}
        </div>
      )}

      {/* Table */}
      <div className={styles.tableWrap}>
        {loading ? (
          <div>
            {[...Array(6)].map((_, i) => (
              <div key={i} className={styles.skeletonRow}>
                <div className={styles.skeletonCell} style={{ width: "14%" }} />
                <div className={styles.skeletonCell} style={{ width: "18%" }} />
                <div className={styles.skeletonCell} style={{ width: "12%" }} />
                <div className={styles.skeletonCell} style={{ width: "12%" }} />
                <div className={styles.skeletonCell} style={{ width: "10%" }} />
                <div className={styles.skeletonCell} style={{ width: "12%" }} />
                <div className={styles.skeletonCell} style={{ width: "10%" }} />
              </div>
            ))}
          </div>
        ) : sortedRows.length === 0 ? (
          <div className={styles.emptyState}>
            <FileText size={46} className={styles.emptyIcon} />
            <h3 className={styles.emptyTitle}>No invoice documents found</h3>
            <p className={styles.emptySubtitle}>
              {hasActiveFilters
                ? "Try adjusting or clearing the filters above."
                : "Sales invoices, credit notes and debit notes will appear here once generated."}
            </p>
          </div>
        ) : (
          <div className={styles.tableScroll}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <SortableTh label="Document #" sortKey="invoiceNumber" sort={sort} onSort={onSort} />
                  <SortableTh label="Type" sortKey="docTypeLabel" sort={sort} onSort={onSort} />
                  <SortableTh label="Customer" sortKey="customerName" sort={sort} onSort={onSort} />
                  <SortableTh label="Booking Ref" sortKey="bookingReference" sort={sort} onSort={onSort} />
                  <SortableTh label="Issue Date" sortKey="issueDate" sort={sort} onSort={onSort} />
                  <SortableTh label="VAT Type" sortKey="vatType" sort={sort} onSort={onSort} />
                  <SortableTh label="Subtotal" sortKey="subtotal" sort={sort} onSort={onSort} align="right" />
                  <SortableTh label="VAT" sortKey="vatAmount" sort={sort} onSort={onSort} align="right" />
                  <SortableTh label="Total" sortKey="amount" sort={sort} onSort={onSort} align="right" />
                  <SortableTh label="Status" sortKey="status" sort={sort} onSort={onSort} />
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <span className={styles.cellPrimary}>{r.invoiceNumber || "—"}</span>
                      {r.linkedInvoiceNumber && (
                        <span className={styles.cellSub}>↳ {r.linkedInvoiceNumber}</span>
                      )}
                    </td>
                    <td><DocTypeBadge subtype={r.docSubtype} label={r.docTypeLabel} /></td>
                    <td className={styles.cellPrimary}>{r.customerName || "—"}</td>
                    <td>
                      {r.bookingReference ? (
                        <span
                          className={styles.cellRef}
                          onClick={() => navigate(`/bookings/${r.bookingReference}`)}
                          title="Open booking"
                        >
                          {r.bookingReference}
                        </span>
                      ) : (
                        <span className={styles.cellMuted}>—</span>
                      )}
                    </td>
                    <td>{formatReportDate(r.issueDate)}</td>
                    <td>
                      <VatChip vatType={r.vatType} />
                      {r.vatType === "TOMS" && r.tomsMargin !== 0 && (
                        <span className={styles.cellSub}>
                          TOMS Margin (VAT basis): {formatEUR(r.tomsMargin)}
                        </span>
                      )}
                    </td>
                    <td className={styles.tdRight}>{formatEUR(r.subtotal)}</td>
                    <td className={styles.tdRight}>{formatEUR(r.vatAmount)}</td>
                    <td className={styles.tdRight}>
                      <span className={styles.money}>{formatEUR(r.amount)}</span>
                      <StaleChip row={r} />
                    </td>
                    <td><StatusBadge status={r.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Export */}
      <div className={styles.exportCard}>
        <div className={styles.exportInfo}>
          <div className={styles.exportInfoIcon}><Download size={18} /></div>
          <div>
            <div className={styles.exportInfoTitle}>Export Invoice Report</div>
            <div className={styles.exportInfoSub}>
              Download the {sortedRows.length} document{sortedRows.length === 1 ? "" : "s"} shown, in your preferred format.
            </div>
          </div>
        </div>
        <div className={styles.exportActions}>
          <button className={`${styles.exportAction} ${styles.actCsv}`} onClick={handleCsv} disabled={loading} type="button">
            <Download size={16} /> CSV
          </button>
          <button className={`${styles.exportAction} ${styles.actExcel}`} onClick={handleExcel} disabled={loading} type="button">
            <FileSpreadsheet size={16} /> Excel
          </button>
          <button className={`${styles.exportAction} ${styles.actPdf}`} onClick={handlePdf} disabled={loading} type="button">
            <FileText size={16} /> PDF
          </button>
          <button className={`${styles.exportAction} ${styles.actPrint}`} onClick={handlePrint} disabled={loading} type="button">
            <Printer size={16} /> Print
          </button>
        </div>
      </div>
    </div>
  );
};

export default InvoiceReport;
